import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { MFASetup } from './pages/MFASetup';
import { MFAVerify } from './pages/MFAVerify';
import { Dashboard } from './pages/Dashboard';
import { PendingRegistrations } from './pages/PendingRegistrations';
import { ClientManagement } from './pages/ClientManagement';
import { ClientDetail } from './pages/ClientDetail';
import { ProductManagement } from './pages/ProductManagement';
import { ProductConfiguration } from './pages/ProductConfiguration';
import { PaymentMethods } from './pages/PaymentMethods';
import { IntegrationRoutes } from './pages/IntegrationRoutes';
import { IntegrationRouteEditor } from './pages/IntegrationRouteEditor';
import { MappingEditor } from './pages/MappingEditor';
import { ConfigurationManager } from './pages/ConfigurationManager';
import { ApiLogs } from './pages/ApiLogs';
import { AuditLogs } from './pages/AuditLogs';
import { SupportTickets } from './pages/SupportTickets';
import { TicketDetail } from './pages/TicketDetail';
import { NotificationTemplates } from './pages/NotificationTemplates';
import { AlertConfiguration } from './pages/AlertConfiguration';
import { SandboxMocks } from './pages/SandboxMocks';
import { CorsApprovals } from './pages/CorsApprovals';
import { AdminUsers } from './pages/AdminUsers';
import { SubscriptionsManagement } from './pages/SubscriptionsManagement';
import { KongClusterSync } from './pages/KongClusterSync';
import { WebhookDeliveries } from './pages/WebhookDeliveries';

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/mfa-setup" element={<MFASetup />} />
          <Route path="/mfa-verify" element={<MFAVerify />} />

          {/* Protected routes with layout */}
          <Route element={<Layout />}>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/registrations"
              element={
                <ProtectedRoute>
                  <PendingRegistrations />
                </ProtectedRoute>
              }
            />

            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <ClientManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/clients/:id"
              element={
                <ProtectedRoute>
                  <ClientDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <SubscriptionsManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin-users"
              element={
                <ProtectedRoute>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />

            <Route
              path="/products"
              element={
                <ProtectedRoute>
                  <ProductManagement />
                </ProtectedRoute>
              }
            />

            <Route
              path="/products/:id"
              element={
                <ProtectedRoute>
                  <ProductConfiguration />
                </ProtectedRoute>
              }
            />

            <Route
              path="/payment-methods"
              element={
                <ProtectedRoute>
                  <PaymentMethods />
                </ProtectedRoute>
              }
            />

            <Route
              path="/kong-sync"
              element={
                <ProtectedRoute>
                  <KongClusterSync />
                </ProtectedRoute>
              }
            />

            <Route
              path="/webhooks-dlq"
              element={
                <ProtectedRoute>
                  <WebhookDeliveries />
                </ProtectedRoute>
              }
            />

            <Route
              path="/integration-routes"
              element={
                <ProtectedRoute>
                  <IntegrationRoutes />
                </ProtectedRoute>
              }
            />

            <Route
              path="/integration-routes/new"
              element={
                <ProtectedRoute>
                  <IntegrationRouteEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/integration-routes/:id"
              element={
                <ProtectedRoute>
                  <IntegrationRouteEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/integration-routes/:id/mappings"
              element={
                <ProtectedRoute>
                  <MappingEditor />
                </ProtectedRoute>
              }
            />

            <Route
              path="/configuration"
              element={
                <ProtectedRoute>
                  <ConfigurationManager />
                </ProtectedRoute>
              }
            />

            <Route
              path="/api-logs"
              element={
                <ProtectedRoute>
                  <ApiLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute>
                  <AuditLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportTickets />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support/:id"
              element={
                <ProtectedRoute>
                  <TicketDetail />
                </ProtectedRoute>
              }
            />

            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationTemplates />
                </ProtectedRoute>
              }
            />

            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <AlertConfiguration />
                </ProtectedRoute>
              }
            />

            <Route
              path="/sandbox-mocks"
              element={
                <ProtectedRoute>
                  <SandboxMocks />
                </ProtectedRoute>
              }
            />

            <Route
              path="/cors-approvals"
              element={
                <ProtectedRoute>
                  <CorsApprovals />
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Catch all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
